import { ELSToastService, ELSWithToastService } from '@els/els-component-toast-react';
import React, { ReactNode } from 'react';
import { orderBy } from 'lodash';
import { ELSCommonConfig, ELSStorageHelper, ELSTokenHelper } from '@els/els-ui-common-react';
import moment from 'moment/moment';
import {
  BankCourseEntryContextDto,
  BookPlatformDto,
  BreadCrumbDto,
  BreadCrumbTypeDto,
  SparkAppLinkContextDto,
  SparkEntryContextDto,
  ChatContextTypeDto,
  ChatDto,
  ChatEntitlementDto,
  ChatEntitlementTypeDto,
  ChatEntryAuthorDto,
  ChatEntryDto,
  ChatEntryEvaluationDto,
  ChatEntryStatus,
  ChatEntryTypeDto,
  ChatQueryIntentDto,
  ChatReferenceBookChunkContentPropsDto,
  ChatReferenceClassDto,
  ChatReferenceDto,
  ChatReferenceTypeDto,
  ChatReferenceVideoContentPropsDto,
  ChatReferenceWithParsedBookContentProps,
  ChatResourceTypeDto,
  ChatStatusDto,
  ChatTypeDto,
  ChatUserRoleDto,
  ContextRequestDto,
  EbookCfiSsoUrlRequestDto,
  EbookSsoUrlRequestDto,
  EbookSsoUrlResponseDto,
  EntryContextDto,
  EvaluationUserTypeDto,
  FinalPromptType,
  FollowUpQuestionDto,
  HMPlatformVectorContentTypeDto,
  IframeEntryContextDto,
  ImageChunkPropsDto,
  ImageRelatedContentPropsDto,
  OsmosisAppLinkContextDto,
  OsmosisEntryContextDto,
  OsmosisMarketDto,
  OsmosisPaidStatusDto,
  PLPAppLinkContextDto,
  PlpEntryContextDto,
  PostProcessJobDto,
  PostProcessJobStatusDto,
  PostProcessJobTypeDto,
  PromptOverrideDto,
  RelatedContentDto,
  RelatedContentType,
  SaiUserEntryContextDto,
  SearchOverrideDto,
  ShellsDto,
  VideoObjectStructuredDataDto,
  VideoRelatedContentPropsDto,
  VstAppLinkContextDto,
  VstEntryContextDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import { FeatureFlagsGroupedDto } from '../../apis/eols-features-api/eols-features-api.dtos';
import { getFeatureFlagGroups, isFeatureFlagEnabled, } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { apStyleTitleCase } from '../../utilities/title-case.utilities';
import {
  ALLOWED_MESSAGE_ORIGINS,
  CHAT_MODE_TYPE_STORAGE_KEY,
  ChatModeType,
  TEMP_BOT_CHAT_ID,
  TEMP_USER_ENTRY_ID,
  isValidChatModeType,
  osmosisVideoUrl,
} from './ai-chat.constants';
import { AppContextState } from '../../components/app/AppProvider.component';
import { AppState } from '../../components/app/app-state.types';
import { isInstructor } from '../../utilities/common.utilities';
import { AiChatState } from './AiChat.page';
import { getTokenIsbns, getTokenIsbnsArray } from '../../components/app/app-state.utilities';
import { getIsTestUser, getUserEmailDomain } from '../../components/app/app-state-selectors.utilities';
import { AppLinkData } from '../../apis/eols-app-link/eols-app-link.dtos';
import {
  AnalyticsActionProps,
  NewRelicAnalyticsAction,
  PostProcessJobBailOutReason
} from '../../models/new-relic-analytics.models';
import {
  fetchEbookCfiSsoUrlAction,
  fetchEbookSsoUrlAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import FocusTrap from 'focus-trap-react';
import AiChatBookFilters, { AiChatBookFiltersModalId } from './AiChatBookFilters.component';
import { ELSModalService } from '@els/els-component-modal-react';
import { RecordAny } from '../../models/common.models';
import { Brand, BrandNameMap } from '../../constants/common.constants';
import { EnvProfile, ServerConstants } from '../../components/app/server.constants';

export const getAdminOverrideFromStorage = (): {
  promptOverride: PromptOverrideDto;
  searchOverride: SearchOverrideDto;
  adminOverridePageContext: string;
} => {
  return ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
    .namespace('')
    .get('adminOverride');
};

export const putAdminOverrideInStorage = (adminOverrides: {
  promptOverride: PromptOverrideDto;
  searchOverride: SearchOverrideDto;
  adminOverridePageContext: string;
}): void => {
  ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
    .namespace('')
    .put('adminOverride', adminOverrides);
};

export const getCitations = (entry: ChatEntryDto): ChatReferenceDto[] => {
  if (!entry?.references?.length) {
    return null;
  }

  return entry.references
    .filter((reference) => {
      return reference.isVisible
        && reference.referenceClass === ChatReferenceClassDto.CITATION;
    }) as ChatReferenceDto[];
};

export const safeParse = (str: string): RecordAny => {
  let val;
  try {
    val = JSON.parse(str);
  } catch (e) {
    val = null;
  }
  return val;
}

export const getParsedContentProps = (citation: ChatReferenceDto): RecordAny => {
  if (!citation?.contentProps) {
    return null;
  }
  return safeParse(citation.contentProps);
};

export const getParsedContentPropsBookChunk = (citation: ChatReferenceDto): ChatReferenceBookChunkContentPropsDto => {
  return getParsedContentProps(citation) as unknown as ChatReferenceBookChunkContentPropsDto
};

export const getParsedContentPropsVideo = (citation: ChatReferenceDto): ChatReferenceVideoContentPropsDto => {
  return getParsedContentProps(citation) as unknown as ChatReferenceVideoContentPropsDto
};

/**
 * Extract the first chunk's properties from ImageRelatedContentPropsDto
 * This handles the new structure where chunkIds and chunkIdToProps are used
 */
export const getImageChunkProps = (imageProps: ImageRelatedContentPropsDto): ImageChunkPropsDto | null => {
  if (!imageProps?.chunkIds || imageProps.chunkIds.length === 0) {
    return null;
  }
  const [firstChunkId] = imageProps.chunkIds;
  return imageProps.chunkIdToProps?.[firstChunkId] || null;
};

export const getRelatedContentVideoDurationDisplay = (relatedContent: RelatedContentDto): string => {
  if (!relatedContent) {
    return null;
  }
  const contentProps = relatedContent.contentProps as VideoRelatedContentPropsDto;

  if (!contentProps) {
    return null;
  }

  const videoObjectStructuredData = safeParse(contentProps?.metadata_videoObjectStructuredData);

  if (!videoObjectStructuredData?.duration) {
    return null;
  }

  // Parse ISO 8601 duration format (e.g., "PT5M20S")
  const duration = String(videoObjectStructuredData.duration);
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = duration.match(regex);

  if (!matches) {
    return null;
  }

  const hours = parseInt(matches[1] || '0', 10);
  const minutes = parseInt(matches[2] || '0', 10);
  const seconds = parseInt(matches[3] || '0', 10);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const getValidStartTimeSeconds = (startTimeSeconds?: number | null): number | null => {
  // t=0 is kept on purpose: it is NACS's fallback when the timestamp lookup fails (FLOR-3180)
  if (typeof startTimeSeconds !== 'number' || !Number.isFinite(startTimeSeconds) || startTimeSeconds < 0) {
    return null;
  }
  return Math.floor(startTimeSeconds);
};

export const getOsmosisRelatedVideoEmbedUrl = (
  videoId: string,
  authToken: string,
  startTimeSeconds?: number | null
): string => {
  const embedUrl = `${osmosisVideoUrl}/embed/${encodeURIComponent(videoId)}?auth=${authToken}`;
  const t = getValidStartTimeSeconds(startTimeSeconds);
  return t === null ? embedUrl : `${embedUrl}&t=${t}`;
};

export const getOsmosisLearnUrl = (learnUrl: string, startTimeSeconds?: number | null): string => {
  const t = getValidStartTimeSeconds(startTimeSeconds);
  if (t === null) {
    return learnUrl;
  }
  // String concat, not new URL(), so the slug path (parentheses, colons) is not re-encoded
  const separator = learnUrl.includes('?') ? '&' : '?';
  return `${learnUrl}${separator}t=${t}`;
};

export const getOsmosisVideoId =(reference: ChatReferenceDto): string => {
  if (!reference) {
    return null;
  }

  if (reference.type === ChatReferenceTypeDto.VIDEO) {
    return reference.contentId;
  }

  // Everything from here down is old and for backwards compatibility
  const uriPrefix = 'https://osmosis.org/learn/';
  const uriPrefix2 = 'https://www.osmosis.org/learn/';
  const contentProps = getParsedContentPropsVideo(reference);

  let resourceUri: string;
  if (contentProps?.url) {
    resourceUri = contentProps.url;
  } else if (contentProps?.location) {
    resourceUri = contentProps.location;
  } else if (contentProps?.source_id) {
    resourceUri = contentProps.source_id;
  } else if (reference.resourceUri) {
    ({ resourceUri } = { resourceUri: reference.resourceUri });
  }

  if (!resourceUri) {
    return null;
  }

  if (resourceUri.includes(uriPrefix)) {
    return resourceUri.replace(uriPrefix, '');
  }

  if (resourceUri.includes(uriPrefix2)) {
    return resourceUri.replace(uriPrefix2, '');
  }
  return null;
};

export const getRecommendations = (entry: ChatEntryDto): ChatReferenceDto[] => {
  if (!entry?.references?.length) {
    return null;
  }

  return entry.references.filter((reference) => {
    const videoId = getOsmosisVideoId(reference);
    return reference.isVisible
      && reference.referenceClass === ChatReferenceClassDto.RECOMMENDATION
      && reference.resourceType === ChatResourceTypeDto.VIDEO
      && videoId;
  }) as ChatReferenceDto[];
};

export const toastApiError = (toastService: ELSToastService): void => {

  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        <p>Request Error</p>
        <p>Please refresh the page and try again. If the problem persists please contact your administrator.</p>
      </div>
    ),
    type: ELSWithToastService.types.NEGATIVE
  });
};

export const toastActiveStreamingError = (props: AppContextState & {
  toastService: ELSToastService;
  trackNewRelicAction: (props: {
    appStateRef: React.MutableRefObject<AppState>;
    actionProps: AnalyticsActionProps;
  }) => void;
  actionRef: string;
}): void => {

  const {
    toastService,
    actionRef
  } = props;

  props.trackNewRelicAction({
    appStateRef: props.appStateRef,
    actionProps: {
      action: NewRelicAnalyticsAction.AI_CHAT_ACTION_WHILE_STREAMING_ERROR,
      props: {
        actionRef
      }
    }
  });

  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        <p>This action cannot be performed while the current prompt is being answered</p>
      </div>
    ),
    type: ELSWithToastService.types.NEGATIVE
  });
};

export const toastCreateChatError = (toastService: ELSToastService): void => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        <p>A new chat cannot be created if you are already in a new chat.</p>
      </div>
    ),
    type: ELSWithToastService.types.NEGATIVE
  });
};

export const getEbookCitationPathsOLD = (citation: ChatReferenceDto): string[] => {
  if (citation.resourceType !== ChatResourceTypeDto.EBOOK) {
    return [];
  }
  if (!citation || !citation.uri) {
    return [];
  }
  const chatperSeperator = 'chapter:';
  const paths = citation.uri.split(chatperSeperator).reduce((acc, cur) => {
    if (!cur) {
      return acc;
    }
    return [
      ...acc,
      `${chatperSeperator}${cur}`
    ];
  }, []);
  return paths.map((path) => {
    return path.split('|').filter((part) => {
      return !!part;
    }).map((part) => {
      return apStyleTitleCase(part.toLowerCase(), null);
    }).map((part) => {
      return part
        .replace('Chapter:', 'Chapter: ')
        .replace('Section:', 'Section: ')
        .replace('Subsection:', 'Subsection: ')
        .replace('Step:', 'Step: ');
    })
      .join(' | ');
  });
};
export const getCitationDisplayOLD = (citation: ChatReferenceDto): ReactNode => {
  if (citation.resourceType === ChatResourceTypeDto.EBOOK) {
    const paths = getEbookCitationPathsOLD(citation);
    return (
      <div>
        <div>{citation.resourceTitle}</div>
        <div>
          {paths?.length > 0 && paths.map((path) => {
            return (
              <div
                key={path}
              >
                {path}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return citation.title;
};

export const getIsISBNCitationLinksDisabled = (
  appState: AppState,
  isbn: string
): boolean => {
  if (!appState.featureFlagsGrouped || !isbn) {
    return false;
  }
  const disabledIsbns = getFeatureFlagGroups(appState.featureFlagsGrouped, FEATURE_FLAG.IS_CITATION_LINKS_DISABLED);
  return disabledIsbns.includes(isbn);
};

export const getIsDirectAccessByFeatureFlag = (
  featureFlagsGrouped: FeatureFlagsGroupedDto[],
  courseSectionId: string,
  isbns: string[],
): boolean => {

  if (!featureFlagsGrouped || !courseSectionId || !isbns?.length) {
    return false;
  }

  const featureIsbns = getFeatureFlagGroups(featureFlagsGrouped, FEATURE_FLAG.IS_DIRECT_CHATBOT_ISBN);

  if (!featureIsbns || !featureIsbns.length) {
    return false;
  }

  return featureIsbns.some((isbn) => {
    return isbns.some((_isbn) => {
      return _isbn === isbn;
    });
  });
};

export const extractNumber = (str: string): number => {
  const matches = str.match(/\((\d+)\)[^()]*$/);
  return matches ? parseInt(matches[1], 10) : null;
};

export const isShowEntryControls = (entry: ChatEntryDto): boolean => {
  return entry.author === ChatEntryAuthorDto.BOT
    && entry.id
    && entry.id !== TEMP_BOT_CHAT_ID
    && (
      !entry._status
      || [ChatEntryStatus.POST_PROCESSING, ChatEntryStatus.COMPLETE].includes(entry._status)
    );
};

export const getSortedChats = (chats: ChatDto[]): ChatDto[] => {
  if (!chats) {
    return null;
  }
  return orderBy(chats, [
    (chat) => {
      if (!chat.lastAccessed) {
        return -1
      }
      return new Date(chat.lastAccessed).getTime();
    },
    (chat) => {
      return chat.id;
    }
  ], ['desc', 'desc']);
};

export const getAfterChapterString = (breadCrumbs: BreadCrumbDto[]): string => {
  if (!breadCrumbs) {
    return null;
  }

  const index = breadCrumbs.findIndex(breadcrumb => breadcrumb.type === 'CHAPTER');
  const parts = index === -1 ? breadCrumbs : breadCrumbs.slice(index + 1);

  if (!parts || !parts.length) {
    return null;
  }

  const defaultDelineator = '; ';
  const deliniaterMap = {
    SECTION: defaultDelineator,
    SUBSECTION: ' - ',
  };

  return parts.reduce((acc, breadcrumb, idx, arr) => {
    if (!breadcrumb.title) {
      return acc;
    }
    const delineator = idx === arr.length - 1 ? '' : deliniaterMap[breadcrumb.type] || defaultDelineator;
    return `${acc}${breadcrumb.title}${delineator}`;
  }, '');
};

// Two distinct chunks (different chunk_id) can share the same page range, chapter,
// and breadcrumb path when a subsection spans only one page. Citations sharing all of
// this are NOT duplicates to be dropped (each is independently cited text) but the
// panel needs to flag them as landing on the same page so the numbering isn't
// mistaken for an error.
export const isSamePageCitation = (
  existingCitation: ChatReferenceWithParsedBookContentProps,
  citationToFind: ChatReferenceWithParsedBookContentProps
): boolean => {
  const { _contentProps: existingProps } = existingCitation;
  const { _contentProps: newProps } = citationToFind;

  let pageRangesMatch;

  try {
    pageRangesMatch = existingProps.page.start_page.number === newProps.page.start_page.number
      && existingProps.page.end_page.number === newProps.page.end_page.number;
  } catch (e) {
    pageRangesMatch = false;
  }

  if (existingProps.chapter_number !== newProps.chapter_number
    || !pageRangesMatch
    || existingProps.bread_crumbs.length !== citationToFind._contentProps.bread_crumbs.length) {
    return false;
  }

  return existingProps.bread_crumbs.every((crumb, idx) => {
    return crumb.id === newProps.bread_crumbs[idx].id
      && crumb.type === newProps.bread_crumbs[idx].type
      && crumb.title === newProps.bread_crumbs[idx].title;
  });
};

export const getCitationGroups = (citations: ChatReferenceDto[]): Record<string, ChatReferenceWithParsedBookContentProps[]> => {
  if (!citations?.length) {
    return {};
  }
  return citations.reduce((acc, cur) => {

    if (cur.resourceType !== ChatResourceTypeDto.EBOOK) {
      return acc;
    }

    const withParsedContentProps = {
      ...cur,
      _contentProps: getParsedContentPropsBookChunk(cur)
    };

    if (!withParsedContentProps._contentProps) {
      return acc;
    }

    const isbn = withParsedContentProps._contentProps.source_id;

    if (acc[isbn]) {
      return {
        ...acc,
        [isbn]: [
          ...acc[isbn],
          withParsedContentProps
        ]
      };
    }
    return {
      ...acc,
      [isbn]: [withParsedContentProps]
    };
  }, {});
};

// Flat, citation-number-ordered list for the References panel. Reuses
// getCitationGroups for EBOOK filtering and content-props parsing, then
// flattens every group into a single array ordered by the inline citation
// number (`index`). This is the internal identity/matching order —
// `index` itself may have gaps (un-cited sources are hidden), but those gaps
// are only ever shown to the user via getCitationDisplayNumberMap below.
export const getOrderedCitations = (
  citations: ChatReferenceDto[]
): ChatReferenceWithParsedBookContentProps[] => {
  const groups = getCitationGroups(citations);
  const flat = Object.keys(groups).reduce(
    (acc, resourceId) => acc.concat(groups[resourceId]),
    [] as ChatReferenceWithParsedBookContentProps[]
  );
  return orderBy(flat, (citation) => citation.index);
};

// Maps each citation's raw (possibly gapped) `index` to its 1-based position
// among the visible, ordered citations, so the pill/panel can show a
// contiguous [1][2][3] sequence to the user instead of the raw index (which
// skips numbers for un-cited, hidden sources). `index` itself is left
// untouched everywhere else (dedup, scroll targets, pill<->panel matching).
export const getCitationDisplayNumberMap = (
  citations: ChatReferenceDto[]
): Map<number, number> => {
  const orderedCitations = getOrderedCitations(citations);
  return new Map(orderedCitations.map((citation, idx) => [citation.index, idx + 1]));
};

export const getStartPageNumber = (citation: ChatReferenceWithParsedBookContentProps): number => {
  let pageNum;
  try {
    if (citation._contentProps.page.start_page.number) {
      pageNum = parseInt(citation._contentProps.page.start_page.number, 10);
    } else {
      pageNum = null;
    }
  } catch (e) {
    pageNum = null;
  }
  return pageNum;
};

export const getEndPageNumber = (citation: ChatReferenceWithParsedBookContentProps): number => {
  let pageNum;
  try {
    if (citation._contentProps.page.end_page.number) {
      pageNum = parseInt(citation._contentProps.page.end_page.number, 10);
    } else {
      pageNum = null;
    }
  } catch (e) {
    pageNum = null;
  }
  return pageNum;
};

export const getPageNumberFromBreadcrumbs = (breadCrumbs: BreadCrumbDto[]): number => {
  if (!breadCrumbs || !breadCrumbs.length) {
    return 1;
  }
  const last = breadCrumbs[breadCrumbs.length - 1];
  if (!last?.page_range?.length) {
    return 1;
  }
  return last.page_range[last.page_range.length - 1];
};

export const isAdminUser = (): boolean => {
  const user = ELSTokenHelper.getUser();
  return Boolean(user?.appParams?.adminEmailAddress);
};

export const getNormalizedEmailDomain = (userEmailDomain: string): string => {
  if (!userEmailDomain) {
    return null;
  }
  return userEmailDomain.trim().toLowerCase();
};

export const getNextEntryEval = (props: {
  existingEntryEval: ChatEntryEvaluationDto;
  userId: string;
  entryId: number;
  newEntryEval: Partial<ChatEntryEvaluationDto>;
}): ChatEntryEvaluationDto => {

  const {
    userId,
    entryId,
    newEntryEval
  } = props;

  const existingEntryEval: ChatEntryEvaluationDto = props.existingEntryEval || {} as ChatEntryEvaluationDto;

  const defaultDto: Omit<ChatEntryEvaluationDto, 'id'> = {
    userId,
    entryId,
    userType: EvaluationUserTypeDto.CLIENT,
    comment: null,
    overall: null,
    comprehension: null,
    correctness: null,
    completeness: null,
    harmfulness: null,
    accuracy: null,
    coherency: null,
    organization: null,
    relevancy: null,
    detail: null,
    entryFeedbackReferences: [],
  };

  if (existingEntryEval.overall === newEntryEval.overall) {
    return {
      ...defaultDto,
      ...existingEntryEval,
      ...newEntryEval,
    };
  }

  return {
    ...existingEntryEval,
    ...defaultDto,
    ...newEntryEval,
  };
};

export const getOrderedPurchasedEntitlements = (entitlements: Partial<Record<ChatEntitlementTypeDto, ChatEntitlementDto[]>>): ChatEntitlementDto[] => {

  if (!entitlements) {
    return [];
  }

  let allEntitlements = [] as ChatEntitlementDto[];
  if (entitlements[ChatEntitlementTypeDto.USER_ENTITLED]) {
    allEntitlements = [
      ...allEntitlements,
      ...entitlements[ChatEntitlementTypeDto.USER_ENTITLED]
    ];
  }

  if (entitlements[ChatEntitlementTypeDto.PILOT_COURSE]) {
    allEntitlements = [
      ...allEntitlements,
      ...entitlements[ChatEntitlementTypeDto.PILOT_COURSE]
    ];
  }

  if (entitlements[ChatEntitlementTypeDto.BANK_ENTITLED]) {
    allEntitlements = allEntitlements.filter((entitlement) => {
      return entitlements[ChatEntitlementTypeDto.BANK_ENTITLED].some((bankEntitlement) => {
        return bankEntitlement.isbn === entitlement.isbn;
      })
    })
  }

  return orderBy(allEntitlements, 'title');
};

export const isShowPerformanceNav = (appState: AppState): boolean => {
  if (getBrand(appState) === Brand.OSMOSIS_AI) {
    // Instructor insights is an institutional feature, so it is limited to b2b. market is
    // optional on the app-link context, so anything other than an explicit b2b fails closed.
    return isFeatureFlagEnabled(FEATURE_FLAG.ENABLE_INSTRUCTOR_INSIGHTS_OSMOSIS_AI_B2B, appState.featureFlagsGrouped, appState.courseSectionId)
      && getOsmosisConfig(appState).osmosisMarket === OsmosisMarketDto.B2B
      && isInstructor(appState.userRole);
  }
  if (isVSTChat(appState)) {
    return false;
  }
  // Spark is a student assessment context, so instructor nav never applies.
  if (isSparkChat(appState)) {
    return false;
  }
  return isInstructor(appState.userRole);
};

// Deliberately a pure flag check: the page is global, with no brand, role or VST gating.
export const isShowQuizQuestionsNav = (appState: AppState): boolean => {
  return isFeatureFlagEnabled(FEATURE_FLAG.ENABLE_QUIZ_QUESTIONS_PAGE, appState.featureFlagsGrouped, appState.courseSectionId);
};

// Deliberately a pure flag check, like isShowQuizQuestionsNav.
export const isShowFlashcardsNav = (appState: AppState): boolean => {
  return isFeatureFlagEnabled(FEATURE_FLAG.ENABLE_FLASHCARDS_PAGE, appState.featureFlagsGrouped, appState.courseSectionId);
};

export const getSortedChatEntries = (chatEntries: ChatEntryDto[]): ChatEntryDto[] => {
  if (!chatEntries) {
    return null;
  }

  return orderBy(chatEntries, ['id', 'index'], 'asc');
};

export const getLastEntry = (chatEntries: ChatEntryDto[]): ChatEntryDto => {
  if (!chatEntries?.length) {
    return null;
  }
  const sortedEntries = getSortedChatEntries(chatEntries);
  return sortedEntries[sortedEntries.length - 1];
};

export const getActiveChat = (state: AppState): ChatDto => {
  const {
    activeChatId,
    chats,
  } = state;
  if (!activeChatId || !chats?.length) {
    return null;
  }
  return chats.find((chat) => {
    return chat.id === activeChatId;
  });
};

export const getSelectedIsbnFilters = (selectedIsbnMap: Record<string, boolean>): string[] => {
  if (!selectedIsbnMap) {
    return null;
  }

  return Object.keys(selectedIsbnMap).filter((isbn => selectedIsbnMap[isbn]));
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const buildTempUserEntry = (
  appState: AppState,
  state: AiChatState,
  chat: ChatDto,
  prompt: string,
  followUpQuestion: FollowUpQuestionDto
): {
  entry: ChatEntryDto;
  iframeContext: ShellsDto["iframeContext"];
  context?: ShellsDto["context"];
} => {

  const {
    appLinkData
  } = appState;

  // state.chatEntries holds persisted entries only -- the optimistic copy lives in
  // state.tempUserEntry instead -- so indexing off the last persisted entry needs no
  // filtering here.
  const lastEntry = getLastEntry(state.chatEntries);
  const attributes: RecordAny = getChatAndEntryAttributes(appState)

  const entry: ChatEntryDto = {
    id: TEMP_USER_ENTRY_ID,
    parentEntryId: null,
    chatId: chat.id,
    index: lastEntry ? lastEntry.index + 1 : 0,
    message: followUpQuestion ? followUpQuestion.question : prompt,
    author: ChatEntryAuthorDto.USER,
    entryType: ChatEntryTypeDto.MESSAGE,
    references: [],
    queryIntent: ChatQueryIntentDto.COMMENT,
    filters: null,
    finalPromptType: null,
    attributes,
  };

  const selectedIsbns = getSelectedIsbnFilters(appState.selectedIsbnMap);

  if (selectedIsbns.length > 0) {
    entry.filters = {
      isbns: selectedIsbns,
      isUseBaseSet: false
    };
  }

  const context = getContextForEntry(appState, state.adminOverridePageContext);

  return {
    entry,
    context,
    iframeContext: !isIframeContext(appLinkData)
      ? null
      : {
        iframeContext: appLinkData?.outPostBody?.iframeContext,
        contentType: appLinkData?.outPostBody?.contentType,
        contentId: appLinkData?.outPostBody?.contentId,
        instanceId: appLinkData?.outPostBody?.instanceId,
        contentItemType: appLinkData?.outPostBody?.contentItemType,
        contentItemId: appLinkData?.outPostBody?.contentItemId,
        iframeContextEntity: appLinkData?.outPostBody?.iframeContextEntity
      }
  };
};

export const getEntryEvaluation = (entry: ChatEntryDto, state: AiChatState): ChatEntryEvaluationDto => {
  if (!entry) {
    return null;
  }
  const {
    chatEvaluations
  } = state;
  if (!chatEvaluations || !chatEvaluations.length) {
    return null;
  }
  return chatEvaluations.find((item) => {
    return item.entryId === entry.id;
  });
};

export const getIsLastItem = (entry: ChatEntryDto, state: AiChatState): boolean => {
  const {
    chatEntries
  } = state;
  if (!chatEntries || !chatEntries.length) {
    return false;
  }
  const orderedChats = getSortedChatEntries(chatEntries);
  return orderedChats[orderedChats.length - 1].id === entry.id;
};

export const getParentEntry = (entry: ChatEntryDto, state: AiChatState): ChatEntryDto => {
  const {
    chatEntries
  } = state;
  if (!entry || !chatEntries || !chatEntries.length) {
    return null;
  }
  return chatEntries.find((chatEntry) => {
    return chatEntry.id === entry.parentEntryId;
  });
};

export const getOsmosisAppLinkContext = (appLinkData: AppLinkData): OsmosisAppLinkContextDto => {
  const rawContext = appLinkData?.outPostBody?.context as Partial<OsmosisAppLinkContextDto> | undefined;
  const context = rawContext?.contextType === ChatContextTypeDto.OSMOSIS_USER ? rawContext : undefined;

  return {
    contextType: ChatContextTypeDto.OSMOSIS_USER,
    pageContext: context?.pageContext ?? null,
    linkBackUrl: context?.linkBackUrl ?? null,
    program: context?.program ?? null,
    bookPlatform: context?.bookPlatform ?? null,
    paidStatus: context?.paidStatus ?? null,
    market: context?.market ?? null,
    productCode: context?.productCode ?? null,
    productID: context?.productID ?? null,
    includeIframeVideoLink: context?.includeIframeVideoLink ?? false,
  };
}

export const getOsmosisContextId = (appState: AppState): string => {
  const osmosisContext = getOsmosisAppLinkContext(appState?.appLinkData);
  const program = osmosisContext.program ?? 'md';
  const bookPlatform = osmosisContext.bookPlatform ?? 'VST';
  return `${program}__${bookPlatform}`;
}

export const getIframeContext = (appState: AppState): string => {
  return appState.appLinkData?.outPostBody?.instanceId || appState.courseSectionId
}

export const getChatContextForNewChat = (appState: AppState): {
  contextId: string;
  contextType: ChatContextTypeDto;
} => {

  const {
    userId,
    appLinkData,
  } = appState;

  const context = appLinkData?.outPostBody?.context;

  if (context?.contextType === ChatContextTypeDto.PLP || context?.contextType === ChatContextTypeDto.VST) {
    const typedContext = context as PLPAppLinkContextDto | VstAppLinkContextDto;
    if (!typedContext.contextId) {
      throw new Error('contextId is required');
    }
    return {
      contextType: typedContext.contextType,
      contextId: typedContext.contextId
    }
  }

  if (context?.contextType === ChatContextTypeDto.SPARK) {
    const typedContext = context as SparkAppLinkContextDto;
    if (!typedContext.contextId) {
      throw new Error('contextId is required');
    }
    return {
      contextType: typedContext.contextType,
      contextId: typedContext.contextId
    }
  }

  if (isBankBasedCourse(appState.entitlements)) {
    return {
      contextId: getTokenIsbns(),
      contextType: ChatContextTypeDto.BANK_COURSE
    }
  }

  if (isOsmosisAI(appLinkData)) {
    return {
      contextType: ChatContextTypeDto.OSMOSIS_USER,
      contextId: getOsmosisContextId(appState)
    }
  }

  // Deprecated
  // TODO Sherpath team to migrate to app link context
  if (isIframeContext(appLinkData)) {
    return {
      contextType: ChatContextTypeDto.IFRAME,
      contextId: getIframeContext(appState)
    }
  }

  return {
    contextId: userId,
    contextType: ChatContextTypeDto.SAI_USER,
  }
}

export const getContextId = (appState: AppState): string => {
  return getChatContextForNewChat(appState).contextId;
}

export const getPageContext = (
  appState: AppState,
  adminOverridePageContext: string,
): string => {
  // Priority 1: Admin override (admin user + admin override currently enabled).
  // Gated on enableAdminOverride (now kept in sync with the ?adminOverride=true URL param)
  // as well as isAdminUser(), so the page-context override stops being applied once the
  // param is removed — consistent with the prompt/search overrides.
  if (appState.enableAdminOverride && isAdminUser() && adminOverridePageContext) {
    return adminOverridePageContext;
  }

  // Priority 2: AppState pageContext (from parent postMessage)
  if (appState.pageContext) {
    return appState.pageContext;
  }

  // Priority 3: App link context.pageContext
  if (appState.appLinkData?.outPostBody?.context?.pageContext) {
    return appState.appLinkData.outPostBody.context.pageContext;
  }

  return null;
}

// Resolves the chat mode an embedder requested via the iframe message API. Deliberately takes
// appState as a plain argument rather than using the isFeatureFlag helper: this is called from a
// message-event closure that is registered once, so a helper capturing `appState` via hook state
// would be stale by the time an embedder posts a message. Do not "simplify" this back to using
// isFeatureFlag(...) — that would reintroduce a stale-closure bug.
export const resolveRequestedChatMode = (
  requestedMode: unknown,
  appState: AppState,
): ChatModeType => {
  if (!isValidChatModeType(requestedMode)) {
    return appState.chatModeType;
  }

  if (requestedMode === ChatModeType.TUTOR_MODE
    && !isFeatureFlagEnabled(FEATURE_FLAG.ENABLE_TUTOR_MODE, appState.featureFlagsGrouped, appState.courseSectionId)) {
    return appState.chatModeType;
  }

  return requestedMode;
};

export const getContextForEntry = (appState: AppState, adminOverridePageContext: string): EntryContextDto => {
  const chatContext = getChatContextForNewChat(appState);
  const pageContext = getPageContext(appState, adminOverridePageContext);
  const isTutorModeFeatureFlagEnabled = isFeatureFlagEnabled(FEATURE_FLAG.ENABLE_TUTOR_MODE, appState.featureFlagsGrouped, appState.courseSectionId);

  const isTutorModeEnabled = isTutorModeFeatureFlagEnabled ? appState.chatModeType === ChatModeType.TUTOR_MODE : false

  if (chatContext.contextType === ChatContextTypeDto.PLP) {
    const context = appState?.appLinkData?.outPostBody?.context as PLPAppLinkContextDto;
    return {
      contextType: chatContext.contextType,
      taxons: context.taxons,
      programType: context.programType,
      isTutorModeEnabled,
      pageContext,
    } satisfies PlpEntryContextDto
  }

  if (chatContext.contextType === ChatContextTypeDto.OSMOSIS_USER) {
    const osmosisContext = getOsmosisAppLinkContext(appState.appLinkData);
    return {
      contextType: chatContext.contextType,
      program: osmosisContext.program,
      pageContext,
      bookPlatform: osmosisContext.bookPlatform,
      isTutorModeEnabled,
      productCode: osmosisContext.productCode,
    } satisfies OsmosisEntryContextDto
  }

  if (chatContext.contextType === ChatContextTypeDto.SAI_USER) {
    return {
      contextType: chatContext.contextType,
      pageContext,
      isTutorModeEnabled,
    } satisfies SaiUserEntryContextDto
  }

  if (chatContext.contextType === ChatContextTypeDto.BANK_COURSE) {
    return {
      contextType: chatContext.contextType,
      pageContext,
      isTutorModeEnabled,
    } satisfies BankCourseEntryContextDto
  }

  if (chatContext.contextType === ChatContextTypeDto.IFRAME) {
    return {
      contextType: chatContext.contextType,
      pageContext,
      isTutorModeEnabled,
    } satisfies IframeEntryContextDto
  }

  if (chatContext.contextType === ChatContextTypeDto.VST) {
    const context = appState?.appLinkData?.outPostBody?.context as VstAppLinkContextDto;
    return {
      contextType: chatContext.contextType,
      isbn: context.isbn,
      pageContext,
      isTutorModeEnabled,
    } satisfies VstEntryContextDto
  }

  if (chatContext.contextType === ChatContextTypeDto.SPARK) {
    const context = appState?.appLinkData?.outPostBody?.context as SparkAppLinkContextDto;
    return {
      contextType: chatContext.contextType,
      contextId: context.contextId,
      // Normalise both fields so the outgoing request agrees with what the client
      // renders: getBookPlatform applies the same VST default used by isCKSLink, and
      // program falls back to null so JSON.stringify keeps the key rather than dropping it.
      // NACS parses this into its own SparkProgramCode enum (a duplicate of Osmosis's
      // program values, not a shared/reused type), so this assumes the Spark launcher
      // sends one of those same string codes (e.g. "md", "do").
      bookPlatform: getBookPlatform(appState),
      program: context.program ?? null,
      pageContext,
      isTutorModeEnabled,
    } satisfies SparkEntryContextDto
  }

  return null;
}

export const getChatContextsForRequest = (appState: AppState): ContextRequestDto[] => {
  const { contextId, contextType } = getChatContextForNewChat(appState);
  return [{
    id: contextId,
    type: contextType,
  }]
}

export const getChatAndEntryAttributes = (appState: AppState): RecordAny => {

  const {
    courseSectionId,
    featureFlagsGrouped,
    userRole,
    ecrId,
  } = appState;

  const userEmailDomain = getUserEmailDomain(appState);
  const isTestUser = getIsTestUser(appState);
  const isbns = getTokenIsbnsArray();
  const { contextId, contextType } = getChatContextForNewChat(appState);

  return {
    isTestUser,
    isMasqueradeUser: ELSTokenHelper.isMasqueradeUser(),
    isDirectAccessByFeatureFlag: getIsDirectAccessByFeatureFlag(featureFlagsGrouped, courseSectionId, isbns),
    emailDomain: getNormalizedEmailDomain(userEmailDomain),
    userRole,
    brand: getBrand(appState),
    courseSectionId,
    ecrId,
    appLinkOutPostBody: appState.appLinkData?.outPostBody ? { ...appState.appLinkData.outPostBody } : undefined,
    contextId,
    contextType,
  }
}

export const getNewDefaultChat = (appState: AppState, chatUserRole: ChatUserRoleDto, title?: string): Omit<ChatDto, 'id'> => {

  const {
    userId,
    ecrId,
  } = appState;

  const { contextId, contextType } = getChatContextForNewChat(appState);

  return {
    createdAt: moment().toISOString(),
    updatedAt: moment().toISOString(),
    title: title ?? 'New chat',
    userId: parseInt(userId, 10),
    contextId,
    contextType,
    ecrId,
    status: ChatStatusDto.ACTIVE,
    entries: null,
    type: ChatTypeDto.FLORENCE_USER_CHAT,
    modelName: null,
    attributes: getChatAndEntryAttributes(appState),
    userRole: chatUserRole
  };
};

export const handleChatScroll = (state: AiChatState, isSmooth = false): void => {

  const {
    chatEntries,
    isAutoScrolling
  } = state;

  if (!chatEntries || chatEntries.length === 0) {
    return;
  }

  if (!isAutoScrolling) {
    return;
  }

  setTimeout(() => {
    const element = document.getElementById('chat-scroll');
    if (element) {
      if (isSmooth) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, 0);

};

export const getDefaultActiveChat = (
  appState: AppState,
  existingActiveChatId: number
): ChatDto => {
  const { chats } = appState;

  if (!chats) {
    return null;
  }

  if (existingActiveChatId) {
    const existingChat = chats.find((chat) => chat.id === existingActiveChatId);
    if (existingChat) {
      return existingChat;
    }
  }

  const chatContext = getChatContextForNewChat(appState);
  if (chatContext.contextType !== ChatContextTypeDto.SAI_USER && chatContext.contextId) {
    return getSortedChats(chats).find((chat) => chat.contextId === chatContext.contextId.toString());
  }

  return getSortedChats(chats).filter((chat) => {
    // Default to existing chat if has emailDomain
    return chat.attributes?.emailDomain;
  })[0];
};

export const resolveEntriesAfterPostProcess = (
  chatEntries: ChatEntryDto[],
  botEntry: ChatEntryDto,
  postProcessedEntry: ChatEntryDto
): ChatEntryDto[] => {
  return chatEntries.map((entry) => {
    if (entry.id === postProcessedEntry.id) {
      return postProcessedEntry;
    }
    if (entry.id === postProcessedEntry.parentEntryId) {
      return {
        ...entry,
        entryType: ChatEntryTypeDto.MESSAGE
      };
    }
    return entry;
  });
};

export const isVSTChat = (appState: AppState): boolean => {
  return appState.appLinkData?.outPostBody?.context?.contextType === ChatContextTypeDto.VST
};

export const getVstLaunchIsbn = (appState: AppState): string | null => {
  const context = appState.appLinkData?.outPostBody?.context;
  if (context?.contextType !== ChatContextTypeDto.VST) {
    return null;
  }
  return (context as VstAppLinkContextDto).isbn || null;
};

export const isSparkChat = (appState: AppState): boolean => {
  return appState.appLinkData?.outPostBody?.context?.contextType === ChatContextTypeDto.SPARK
};

export const isBankBasedCourse = (entitlements: Partial<Record<ChatEntitlementTypeDto, ChatEntitlementDto[]>>): boolean => {
  if (!entitlements) {
    return false;
  }
  return Boolean(entitlements[ChatEntitlementTypeDto.BANK_ENTITLED]);
};

export const isIframeContext = (appLinkData: AppLinkData): boolean => {
  return appLinkData?.outPostBody?.isIframe;
};

export const getBookTitle = (isbn: string, appState: AppState): string => {
  if (!appState.entitlements) {
    return null;
  }

  const allEntitlements = Object.values(appState.entitlements).reduce((acc, cur) => {
    return [
      ...acc,
      ...cur
    ];
  }, []);

  const entitlement = allEntitlements.find((_entitlement) => {
    return _entitlement.isbn === isbn;
  });

  if (!entitlement) {
    return null;
  }

  return getBookTitleWithEdition(entitlement.title, entitlement.edition) || entitlement.isbn || null;
};

export const openBookFilterModal = (props: {
  appStateRef: React.MutableRefObject<AppState>;
  modalService: ELSModalService;
}): void => {

  trackNewRelicAction({
    appStateRef: props.appStateRef,
    actionProps: {
      action: NewRelicAnalyticsAction.BOOK_FILTER_BTN_CLICK,
      props: {
        btn: null,
        selectedIsbn: null
      }
    }
  });

  props.modalService.openCustomModal({
    modalId: AiChatBookFiltersModalId,
    dialogAriaLabel: 'Book filters',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    noFocusTrap: true,
    modal: (
      <FocusTrap focusTrapOptions={{
        preventScroll: true
      }}>
        <div>
          <AiChatBookFilters />
        </div>
      </FocusTrap>
    )
  });
};

export const isOsmosisAI = (appLinkData: AppLinkData): boolean => {
  return appLinkData?.outPostBody?.context?.contextType === ChatContextTypeDto.OSMOSIS_USER;
}

export const getBrand = (appState: AppState): Brand => {

  if (isSparkChat(appState)) {
    return Brand.AI_COACH;
  }

  if (isOsmosisAI(appState.appLinkData)) {
    return Brand.OSMOSIS_AI;
  }

  if (!appState.entitlements) {
    return null;
  }

  if (isVSTChat(appState) || isBankBasedCourse(appState.entitlements)) {
    return Brand.NORA;
  }

  return Brand.SHERPATH_AI;
}

export const getBrandName = (appState: AppState): string => {
  return BrandNameMap[getBrand(appState)] || ''
}

export const getExcerptViewFeatureFlag = (appState: AppState): FEATURE_FLAG => {
  // Check for PLP context first
  if (appState.appLinkData?.outPostBody?.context?.contextType === ChatContextTypeDto.PLP) {
    return FEATURE_FLAG.ENABLE_EBOOK_EXCERPT_VIEW_PLP;
  }

  // Check brand-based contexts
  const brand = getBrand(appState);

  if (brand === Brand.OSMOSIS_AI) {
    return FEATURE_FLAG.ENABLE_EBOOK_EXCERPT_VIEW_OSMOSIS_AI;
  }

  if (brand === Brand.NORA) {
    return FEATURE_FLAG.ENABLE_EBOOK_EXCERPT_VIEW_NORA;
  }

  if (brand === Brand.SHERPATH_AI) {
    return FEATURE_FLAG.ENABLE_EBOOK_EXCERPT_VIEW_SHERPATH_AI;
  }

  // AI Coach (Spark) is a locked-down assessment context with no excerpt-view flag of its
  // own, so it deliberately falls through to null and excerpt view stays off.
  return null;
};

export const getIsExcerptViewEnabled = (appState: AppState): boolean => {
  const excerptViewFlag = getExcerptViewFeatureFlag(appState);

  if (isOsmosisAI(appState.appLinkData)) {
    const osmosisConfig = getOsmosisConfig(appState);
    const isSupportedMarket =
      osmosisConfig.osmosisMarket === OsmosisMarketDto.B2C
      || osmosisConfig.osmosisMarket === OsmosisMarketDto.B2B;
    const isSupportedPaidStatus =
      osmosisConfig.osmosisPaidStatus === OsmosisPaidStatusDto.TRIAL
      || osmosisConfig.osmosisPaidStatus === OsmosisPaidStatusDto.PAID;
    if (
      !isSupportedMarket
      || osmosisConfig.bookPlatform !== BookPlatformDto.VST
      || !isSupportedPaidStatus
    ) {
      return false;
    }
  }

  return excerptViewFlag
    ? isFeatureFlagEnabled(excerptViewFlag, appState.featureFlagsGrouped, appState.courseSectionId)
    : false;
};

/**
 * Book platform for the app-link context, for the two contexts that can use CKS.
 * Anything else reads as VST, which is the platform-agnostic default.
 */
export const getBookPlatform = (appState: AppState): BookPlatformDto => {
  const context = appState?.appLinkData?.outPostBody?.context as
    Partial<OsmosisAppLinkContextDto | SparkAppLinkContextDto> | undefined;

  if (isOsmosisAI(appState?.appLinkData) || isSparkChat(appState)) {
    return (context?.bookPlatform as BookPlatformDto) ?? BookPlatformDto.VST;
  }

  return BookPlatformDto.VST;
};

export const isCKSLink = (
  appState: AppState,
  citation: ChatReferenceWithParsedBookContentProps
): boolean => {
  if (!citation || !citation._contentProps) {
    return false;
  }
  // Osmosis keeps its long-standing behaviour of CKS-linking without an explicit
  // bookPlatform; Spark only deep-links into CKS when the app-link actually says CKS.
  const isCksEligible = isOsmosisAI(appState.appLinkData)
    || (isSparkChat(appState) && getBookPlatform(appState) === BookPlatformDto.CKS);
  return isCksEligible && Boolean(citation._contentProps.content_type
    && citation._contentProps.content_type === HMPlatformVectorContentTypeDto.BK
    && citation._contentProps.eid)
};

export const isCitationClickable
  = (
  appState: AppState,
  citation: ChatReferenceWithParsedBookContentProps
): boolean => {
  if (!citation || !citation._contentProps) {
    return false;
  }

  if (getIsISBNCitationLinksDisabled(appState, citation._contentProps.source_id)) {
    return false;
  }

  // CKS links deep-link via breadcrumb ids/eid and don't need a page number.
  if (isCKSLink(appState, citation)) {
    return true;
  }

  // A VST/ebook citation link lands on the cited page. Without a page number there is nowhere
  // clear to send the user, so don't render it as a link.
  return getIsBookClickable(appState, citation._contentProps.source_id)
    && getStartPageNumber(citation) != null;
}

export const getIsBookClickable = (
  appState: AppState,
  isbn: string
): boolean => {
  // NACS may resolve VST entitlements to a sibling ISBN indexed in vector search (EEO/199
  // mapping, FLOR-3706). When that happens the link's ISBN no longer matches what the student
  // launched with, so it must not be clickable even though the underlying content is valid -
  // this applies to citation links and related-image source links alike.
  if (isVSTChat(appState) && isbn !== getVstLaunchIsbn(appState)) {
    return false;
  }

  return getIsISBNEntitled(appState, isbn) || getIsExcerptViewEnabled(appState);
};

export const getOsmosisConfig = (appState: AppState): {
  isOsmosis: boolean;
  bookPlatform: BookPlatformDto;
  osmosisPaidStatus: string;
  osmosisMarket: string;
} => {
  const osmosisContext = getOsmosisAppLinkContext(appState.appLinkData);

  // Determine if this is an Osmosis context
  const isOsmosis = isOsmosisAI(appState.appLinkData);

  // CKS platform is only for Osmosis; non-Osmosis always uses VST. Spark derives its own
  // platform via getBookPlatform, so this config stays Osmosis-scoped.
  const bookPlatform = (isOsmosis && osmosisContext.bookPlatform ? osmosisContext.bookPlatform as BookPlatformDto : BookPlatformDto.VST);

  const osmosisPaidStatus = osmosisContext.paidStatus;
  const osmosisMarket = osmosisContext.market;

  return {
    isOsmosis,
    bookPlatform,
    osmosisPaidStatus,
    osmosisMarket
  };
};

const buildBaseEbookSsoUrlRequest = (
  props: {
    appState: AppState;
    isbn: string;
    startPage: string;
    endPage: string;
    chunkId: string;
    isNotEntitled: boolean;
    referenceId: number;
    entryId: number;
  }
): EbookSsoUrlRequestDto => {
  const {
    appState,
    isbn,
    startPage,
    endPage,
    chunkId,
    isNotEntitled,
    referenceId,
    entryId,
  } = props;
  const vitalSourceId = 'sher_evol';
  const osmosisConfig = getOsmosisConfig(appState);
  const isExcerptView = getIsExcerptViewEnabled(appState) && isNotEntitled;

  return {
    vitalSourceId,
    isbn,
    startPage,
    endPage,
    chunkId,
    isExcerptView,
    referenceId,
    entryId,
    ...osmosisConfig
  };
};

export type BuildEbookSsoUrlRequestFromCitationParams = {
  appState: AppState;
  citation: ChatReferenceWithParsedBookContentProps;
};

export const buildEbookSsoUrlRequestFromCitation = (params: BuildEbookSsoUrlRequestFromCitationParams): EbookSsoUrlRequestDto => {
  const { appState, citation } = params;

  // Extract values from citation
  const { isbn } = citation._contentProps;
  const startPage = citation._contentProps.page.start_page.number;
  const endPage = citation._contentProps.page.end_page.number || startPage || '1';
  const chunkId = citation.contentId || '';

  // Determine if the user is entitled to this book
  const isNotEntitled = getIsISBNNotEntitled(appState, citation._contentProps.source_id);

  return buildBaseEbookSsoUrlRequest({
    appState,
    isbn,
    startPage,
    endPage,
    chunkId,
    isNotEntitled,
    referenceId: citation.id,
    entryId: citation.entryId
  });
};

/**
 * The result of the CFI SSO request once a rejection has been collapsed into a fulfilled
 * value, so that the page-range fallback can be invoked from exactly one place.
 *
 * isFailure distinguishes "the request rejected" from "the request resolved with no
 * content" (a null response). Callers fall back to the page-range request for either
 * outcome, but only track EBOOK_CFI_LINK_RESULT for the latter — outright failures are
 * already covered by the API_ERROR event the shared request-error handler fires.
 */
export type CfiSsoUrlOutcome = {
  response: EbookSsoUrlResponseDto;
  isFailure: boolean;
};

/**
 * Collapses a rejected CFI SSO request into a resolved "failure" outcome. Keeping this
 * off the promise's rejection path means the caller's page-range fallback runs at most
 * once per click, and a rejection from the fallback itself is the only thing left that
 * can reach the caller's trailing .catch.
 */
export const toCfiSsoUrlOutcome = (
  promise: Promise<EbookSsoUrlResponseDto | null>
): Promise<CfiSsoUrlOutcome> => {
  return promise
    .then((response) => ({ response, isFailure: false }))
    .catch(() => ({ response: null, isFailure: true }));
};

/**
 * Runs the whole "open the ebook at an exact CFI, falling back to the page range"
 * flow, shared by every click handler that links out to an ebook.
 *
 * The two call sites differ only in how they build their requests, so both builders
 * are passed in: cfiRequest is already-built (null when there is no CFI to try) and
 * buildPageRangeRequest is a thunk so the page-range request is only assembled when
 * the fallback is actually needed.
 *
 * The fallback is invoked from exactly one place here (toCfiSsoUrlOutcome collapses a
 * rejected CFI call into a resolved failure rather than leaving it on the rejection
 * path), which is what guarantees at most one page-range request per click.
 *
 * Returns a promise that rejects only when the page-range request itself fails, so
 * callers can attach a single trailing .catch for their error toast.
 *
 * NOTE (popup blockers): on the fallback path this makes two sequential round trips
 * before window.open, which Safari in particular may treat as no longer user-initiated
 * and block silently. Opening a placeholder tab synchronously in the click handler and
 * assigning its location afterwards is the usual remedy; it is deferred here because
 * no other window.open call in this codebase does it, and it needs a real Safari
 * verification pass to confirm the behaviour before changing every ebook link.
 */
export const openEbookAtCfiOrPageRange = (props: AppContextState & {
  cfiRequest: EbookCfiSsoUrlRequestDto;
  buildPageRangeRequest: () => EbookSsoUrlRequestDto;
}): Promise<void> => {
  const { appState, appStateRef, setAppState, cfiRequest, buildPageRangeRequest } = props;

  const openPageRangeFallback = (): Promise<void> => {
    return fetchEbookSsoUrlAction({
      appState,
      appStateRef,
      setAppState,
      request: buildPageRangeRequest()
    })
      .then((response) => {
        window.open(response.signInUrl, '_blank');
      });
  };

  if (!cfiRequest) {
    return openPageRangeFallback();
  }

  return toCfiSsoUrlOutcome(
    fetchEbookCfiSsoUrlAction({ appState, appStateRef, setAppState, request: cfiRequest })
  )
    .then(({ response, isFailure }) => {
      if (!isFailure) {
        // Outright failures are left to the existing API_ERROR event.
        trackNewRelicAction({
          appStateRef,
          actionProps: {
            action: NewRelicAnalyticsAction.EBOOK_CFI_LINK_RESULT,
            props: {
              isbn: cfiRequest.isbn,
              cfiRange: cfiRequest.cfiRange,
              status: response ? 'success' : 'no_content'
            }
          }
        });
      }
      if (response) {
        window.open(response.signInUrl, '_blank');
        return undefined;
      }
      return openPageRangeFallback();
    });
};

/**
 * Picks the CFI off the most specific (last) breadcrumb that has one, walking from the
 * deepest breadcrumb outwards. getPageNumberFromBreadcrumbs likewise resolves its page
 * from the deepest breadcrumb (it reads only the last entry), so both paths anchor on the
 * same end of the breadcrumb trail.
 */
export const getCfiFromBreadcrumbs = (breadCrumbs: BreadCrumbDto[]): string | undefined => {
  return [...(breadCrumbs ?? [])].reverse().find((breadCrumb) => breadCrumb.cfi)?.cfi;
};

export const getCfiFromCitation = (
  citation: ChatReferenceWithParsedBookContentProps
): string | undefined => {
  return getCfiFromBreadcrumbs(citation._contentProps?.bread_crumbs);
};

/**
 * Returns null when the citation carries no CFI or no ISBN, or the user is not on the
 * excerpt experience, signalling to the caller that there is nothing to try before the
 * page-range request.
 */
export const buildEbookCfiSsoUrlRequestFromCitation = (
  params: BuildEbookSsoUrlRequestFromCitationParams
): EbookCfiSsoUrlRequestDto | null => {
  const { appState, citation } = params;
  const cfiRange = getCfiFromCitation(citation);
  const { isbn } = citation._contentProps;
  if (!cfiRange || !isbn) {
    return null;
  }

  // NACS only serves CFI links for the excerpt experience, so entitled users (full ebook)
  // must go straight to the page-range request.
  const isExcerptView = getIsExcerptViewEnabled(appState)
    && getIsISBNNotEntitled(appState, citation._contentProps.source_id);
  if (!isExcerptView) {
    return null;
  }

  const osmosisConfig = getOsmosisConfig(appState);

  return {
    vitalSourceId: 'sher_evol',
    isbn,
    cfiRange,
    isExcerptView,
    referenceId: citation.id,
    entryId: citation.entryId,
    ...osmosisConfig
  };
};

export const getIsISBNNotEntitled = (
  appState: AppState,
  isbn: string | undefined
): boolean => {
  // Handle missing ISBN
  if (!isbn) {
    return true;
  }

  const isbns = getTokenIsbnsArray();
  const isDirectAccessByFeatureFlag = getIsDirectAccessByFeatureFlag(
    appState.featureFlagsGrouped,
    appState.courseSectionId,
    isbns
  );

  if (isDirectAccessByFeatureFlag) {
    return true;
  }

  const entitlements = getOrderedPurchasedEntitlements(appState.entitlements);
  if (!entitlements?.length) {
    return true;
  }

  const foundIsbn = entitlements.find((entitlement) => {
    // TODO: Move this includes to equal when platform fixes ebookData isbns
    return isbn.includes(entitlement.isbn);
  });

  return !foundIsbn?.isbn;
};

export const getIsISBNEntitled = (
  appState: AppState,
  isbn: string | undefined
): boolean => {
  return !getIsISBNNotEntitled(appState, isbn);
};

export const getRelatedImageIsbn = (
  relatedContent: RelatedContentDto
): string | undefined => {
  if (relatedContent.contentType !== RelatedContentType.IM) {
    throw new Error('Related content type not supported');
  }
  const imageProps = relatedContent.contentProps as ImageRelatedContentPropsDto;
  const chunkProps = getImageChunkProps(imageProps);
  return chunkProps?.ebookData?.isbn;
}

export const buildEbookSsoUrlRequestFromRelatedImage = (
  appState: AppState,
  relatedContent: RelatedContentDto
): EbookSsoUrlRequestDto => {

  if (relatedContent.contentType !== RelatedContentType.IM) {
    throw new Error('Related content type not supported');
  }

  const imageProps = relatedContent.contentProps as ImageRelatedContentPropsDto;
  const chunkProps = getImageChunkProps(imageProps);
  const pageStart = getPageNumberFromBreadcrumbs(chunkProps?.breadCrumbs);

  // Extract ISBN and page info from chunk data
  const isbn = chunkProps?.ebookData?.isbn;
  const startPage = chunkProps?.vectorContentProps?.page?.start_page?.number || pageStart?.toString() || '1';
  const endPage = chunkProps?.vectorContentProps?.page?.end_page?.number || pageStart?.toString() || '1';
  const chunkId = imageProps.chunkIds?.[0] || '';

  // Determine if the user is entitled to this book
  const isNotEntitled = getIsISBNNotEntitled(appState, isbn);

  return buildBaseEbookSsoUrlRequest({
    appState,
    isbn,
    startPage,
    endPage,
    chunkId,
    isNotEntitled,
    referenceId: null,
    entryId: relatedContent.entryId
  });
};

/**
 * Returns null when the related image carries no CFI or no ISBN, or the user is not on
 * the excerpt experience, signalling to the caller that there is nothing to try before
 * the page-range request.
 */
export const buildEbookCfiSsoUrlRequestFromRelatedImage = (
  appState: AppState,
  relatedContent: RelatedContentDto
): EbookCfiSsoUrlRequestDto | null => {

  if (relatedContent.contentType !== RelatedContentType.IM) {
    throw new Error('Related content type not supported');
  }

  const imageProps = relatedContent.contentProps as ImageRelatedContentPropsDto;
  const chunkProps = getImageChunkProps(imageProps);
  const cfiRange = getCfiFromBreadcrumbs(chunkProps?.breadCrumbs);
  const isbn = chunkProps?.ebookData?.isbn;
  if (!cfiRange || !isbn) {
    return null;
  }

  // NACS only serves CFI links for the excerpt experience, so entitled users (full ebook)
  // must go straight to the page-range request.
  const isExcerptView = getIsExcerptViewEnabled(appState)
    && getIsISBNNotEntitled(appState, isbn);
  if (!isExcerptView) {
    return null;
  }

  const osmosisConfig = getOsmosisConfig(appState);

  return {
    vitalSourceId: 'sher_evol',
    isbn,
    cfiRange,
    isExcerptView,
    referenceId: null,
    entryId: relatedContent.entryId,
    ...osmosisConfig
  };
};

export const buildEbookSsoUrlRequestFromIsbn = (
  appState: AppState,
  isbn: string
): EbookSsoUrlRequestDto => {
  const startPage = '1';
  const endPage = '1';
  const chunkId = '';

  // Check if user is entitled to this book
  const entitlements = getOrderedPurchasedEntitlements(appState.entitlements);

  const isNotEntitled = !entitlements?.some((entitlement) => entitlement.isbn === isbn);

  return buildBaseEbookSsoUrlRequest({
    appState,
    isbn,
    startPage,
    endPage,
    chunkId,
    isNotEntitled,
    referenceId: null,
    entryId: null
  });
};

export const buildCksUrlFromCitation = (citation: ChatReferenceWithParsedBookContentProps): string => {

  const baseUrl = ServerConstants[ServerConstants.DataSource].cksBaseUrl
  const {
    bread_crumbs,
    eid
  } = citation._contentProps;

  if (!eid) {
    return null
  }

  if (!bread_crumbs || bread_crumbs.length === 0) {
    // No breadcrumbs - link to book
    return `${baseUrl}/content/book/${eid}`;
  }

  const section = bread_crumbs.find(bc => bc.type === BreadCrumbTypeDto.SECTION);
  const chapter = bread_crumbs.find(bc => bc.type === BreadCrumbTypeDto.CHAPTER);

  if (chapter?.id) {
    if (section?.id) {
      return `${baseUrl}/content/book/${chapter.id}#${section.id}`;
    }
    return `${baseUrl}/content/book/${chapter.id}`;
  }

  // Fallback to book if no usable breadcrumbs
  return `${baseUrl}/content/book/${eid}`;
};

export const getChatEntryCount = (entry: ChatEntryDto): number => {
  if (!entry) {
    return 0;
  }
  return Math.ceil(entry.index / 2)
}

export const convertDurationToSeconds = (duration: string): number => {
  // example video estimatedTime = PT14M48S
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = duration.match(regex);

  if (!matches) {
    return 0;
  }

  const hours = parseInt(matches[1] || '0', 10);
  const minutes = parseInt(matches[2] || '0', 10);
  const seconds = parseInt(matches[3] || '0', 10);

  return hours * 3600 + minutes * 60 + seconds;
}

export const getMinutesAndSecondsFromSeconds = (seconds: number): string => {
  const minute = Math.floor(seconds / 60);
  const second = seconds % 60;

  const minuteStr = minute.toString().padStart(2, '0');
  const secondStr = second.toString().padStart(2, '0');

  return `${minuteStr}:${secondStr}`;
}

export const getVideoDurationDisplay = (reference: ChatReferenceDto): string => {
  if (!reference) {
    return null;
  }
  const contentProps = getParsedContentPropsVideo(reference);

  if (!contentProps) {
    return null;
  }

  const metadata = safeParse(contentProps?.metadata_videoObjectStructuredData) as VideoObjectStructuredDataDto

  if (!metadata || !metadata.duration) {
    return null;
  }

  const seconds = convertDurationToSeconds(metadata.duration);
  return getMinutesAndSecondsFromSeconds(seconds);
}

export const getUserName = (appState: AppState): { firstName: string; fullName: string } => {
  const { chatUserRole, eolsUser } = appState;

  if (chatUserRole === ChatUserRoleDto.INSTRUCTOR_AS_STUDENT) {
    return {
      firstName: 'Student',
      fullName: 'Student View'
    };
  }

  const firstName = eolsUser?.firstName || '';
  const lastName = eolsUser?.lastName || '';

  return {
    firstName,
    fullName: `${firstName} ${lastName}`.trim()
  };
};

export const getChatUserRoleFromTokenRole = (tokenRole: string): ChatUserRoleDto => {
  return isInstructor(tokenRole) ? ChatUserRoleDto.INSTRUCTOR : ChatUserRoleDto.STUDENT;
};

export type ParsedImageChunkText = {
  alt?: string;
  caption?: string;
  context?: string;
}

/**
 * Parse the chunkText from ImageRelatedContentPropsDto
 * Expected format:
 * alt: <text>
 * caption: <text>
 * context: <text>
 */
export const parseImageChunkText = (chunkText: string): ParsedImageChunkText => {
  if (!chunkText) {
    return {};
  }

  const result: ParsedImageChunkText = {};
  const lines = chunkText.split('\n');

  let currentField: keyof ParsedImageChunkText = null;
  let currentValue = '';

  for (const line of lines) {
    // Check if line starts with a field marker
    if (line.startsWith('alt:')) {
      if (currentField && currentValue) {
        result[currentField] = currentValue.trim();
      }
      currentField = 'alt';
      currentValue = line.substring(4).trim();
    } else if (line.startsWith('caption:')) {
      if (currentField && currentValue) {
        result[currentField] = currentValue.trim();
      }
      currentField = 'caption';
      currentValue = line.substring(8).trim();
    } else if (line.startsWith('context:')) {
      if (currentField && currentValue) {
        result[currentField] = currentValue.trim();
      }
      currentField = 'context';
      currentValue = line.substring(8).trim();
    } else if (currentField) {
      // Continuation of current field
      currentValue += ' ' + line.trim();
    }
  }

  // Don't forget the last field
  if (currentField && currentValue) {
    result[currentField] = currentValue.trim();
  }

  return result;
}

export const getBreadcrumbsDisplayParts = (breadCrumbs: BreadCrumbDto[]): {
  chapter: string;
  sections: string;
} => {

  const parts = {
    chapter: null,
    sections: null
  }

  if (!breadCrumbs || !breadCrumbs.length) {
    return parts;
  }

  const chapter = breadCrumbs.find((breadcrumb) => {
    return breadcrumb.type === 'CHAPTER';
  });

  if (chapter?.title) {
    parts.chapter = chapter.title
  }

  const afterChapterString = getAfterChapterString(breadCrumbs);

  if (afterChapterString) {
    parts.sections = afterChapterString
  }

  return parts;
};

export const isShowChooseBooks = (appState: AppState): boolean => {
  return !getIsDirectAccessByFeatureFlag(
      appState.featureFlagsGrouped,
      appState.courseSectionId,
      getTokenIsbnsArray()
    )
    && getBrand(appState) !== Brand.OSMOSIS_AI
    // Spark launches against a fixed assessment context, so the My Books picker must not render.
    && getBrand(appState) !== Brand.AI_COACH
    && !isVSTChat(appState)
    && getOrderedPurchasedEntitlements(appState.entitlements)?.length > 1
    && !appState.appLinkData?.outPostBody?.disabledMyBooks
}

export const getPostProcessJobsByType = (
  entry: ChatEntryDto,
  jobType: PostProcessJobTypeDto
): PostProcessJobDto[] => {
  return (entry?.postProcessJobs || []).filter((job) => job.jobType === jobType);
};

export const getPostProcessJob = (
  entry: ChatEntryDto,
  jobType: PostProcessJobTypeDto
): PostProcessJobDto | undefined => {
  return (entry?.postProcessJobs || []).find((job) => job.jobType === jobType);
};

export const isPostProcessJobInProgress = (job: PostProcessJobDto | undefined): boolean => {
  if (!job) {
    return false;
  }
  return job.status === PostProcessJobStatusDto.NOT_STARTED
    || job.status === PostProcessJobStatusDto.IN_PROGRESS;
};

export const isInputBlocked = (appState: AppState): boolean => {
  return appState.isLoading || (appState.inProgressPostProcessJobIds?.length || 0) > 0;
};

// For bot entries that trigger a post-process job (e.g. flashcards), citations
// must wait for that job to reach COMPLETED. Otherwise citations would flash
// on screen between the bot reply arriving and the polling result coming back.
const CITATIONS_REQUIRED_JOB_BY_PROMPT_TYPE: Partial<Record<FinalPromptType, PostProcessJobTypeDto>> = {
  [FinalPromptType.FLASHCARD_ACKNOWLEDGMENT]: PostProcessJobTypeDto.GENERATE_FLASHCARDS,
  [FinalPromptType.QUIZ_QUESTION_ACKNOWLEDGMENT]: PostProcessJobTypeDto.GENERATE_QUIZ_QUESTIONS,
};

// The post-process jobs NACS spawns for each bot-response prompt type. This is the
// single source of truth for what the polling coordinator waits on — the entry's
// finalPromptType maps directly to its expected jobs. Types that spawn no jobs
// (declines, errors) map to []. Being a total Record, adding a FinalPromptType
// without a mapping is a compile error.
const JOBS_BY_PROMPT_TYPE: Record<FinalPromptType, PostProcessJobTypeDto[]> = {
  // No-job types: declines and errors carry no citations, references, or media.
  [FinalPromptType.DECLINE_RESPONSE_FAILED_CLASSIFICATION]: [],
  [FinalPromptType.DECLINE_RESPONSE_NO_SEARCH_RESULTS]: [],
  [FinalPromptType.ERROR]: [],
  // Can Respond without citations
  [FinalPromptType.RESPOND_WITHOUT_CITATIONS]: [PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS],
  // Cited primary / MCQ responses get the full set.
  [FinalPromptType.PRIMARY_RESPONSE]: [
    PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS,
    PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS,
  ],
  [FinalPromptType.PRIMARY_RESPONSE_TABLE_FALLBACK]: [
    PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS,
    PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS,
  ],
  [FinalPromptType.MCQ_RESPONSE]: [
    PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS,
    PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS,
  ],
  // Tutor mode is identical to a primary response minus follow-up questions
  // (explicitly guarded backend-side).
  [FinalPromptType.PRIMARY_RESPONSE_TUTOR_MODE]: [
    PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS,
  ],
  // Decline to answer / can respond without citations
  // Content-retrieval acknowledgments carry media only — no references or follow-ups.
  [FinalPromptType.CONTENT_RETRIEVAL_ACKNOWLEDGMENT]: [
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_IMAGES,
    PostProcessJobTypeDto.RETRIEVE_RELATED_CONTENT_VIDEOS,
  ],
  // Learning-utility acknowledgments produce their generation job plus follow-up
  // questions (FLOR-3898), but never FILTER_RELEVANT_REFERENCES — the acknowledgment
  // text cites nothing, and NACS resolves the generated content's references directly
  // once generation completes.
  [FinalPromptType.FLASHCARD_ACKNOWLEDGMENT]: [
    PostProcessJobTypeDto.GENERATE_FLASHCARDS,
    PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS,
  ],
  [FinalPromptType.QUIZ_QUESTION_ACKNOWLEDGMENT]: [
    PostProcessJobTypeDto.GENERATE_QUIZ_QUESTIONS,
    PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS,
  ],
};

// Prompt types that do NOT trigger a FILTER_RELEVANT_REFERENCES job.
// Citations must be suppressed until the job record arrives via polling.
export const FILTER_RELEVANT_REFERENCES_EXEMPT_PROMPT_TYPES: FinalPromptType[] = (
  Object.keys(JOBS_BY_PROMPT_TYPE) as FinalPromptType[]
).filter((promptType) => !JOBS_BY_PROMPT_TYPE[promptType].includes(PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES));

export const shouldShowCitations = (
  entry: ChatEntryDto | undefined,
  isCitationsLoading: boolean,
  isFromInitialLoad = false
): boolean => {
  if (!entry || entry.author === ChatEntryAuthorDto.USER) {
    return false;
  }

  const requiredJobType = CITATIONS_REQUIRED_JOB_BY_PROMPT_TYPE[entry.finalPromptType];
  if (requiredJobType) {
    const job = getPostProcessJob(entry, requiredJobType);
    if (job?.status !== PostProcessJobStatusDto.COMPLETED) {
      return false;
    }
  }

  // Show nothing until the FILTER_RELEVANT_REFERENCES job arrives, show a spinner
  // while it's in progress, and only reveal citations once it's terminal. History
  // entries predating the FILTER_RELEVANT_REFERENCES job (FLOR-3507) never get one
  // and are never polled — treat a missing job on those the same as an exempt
  // prompt type instead of hiding citations forever.
  const filterRefsJob = getPostProcessJob(entry, PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES);
  const expectsFilterRefsJob = !FILTER_RELEVANT_REFERENCES_EXEMPT_PROMPT_TYPES.includes(entry.finalPromptType)
    && !(isFromInitialLoad && !filterRefsJob);
  if (expectsFilterRefsJob) {
    if (!filterRefsJob) {
      return false;
    }
    if (!isPostProcessJobTerminal(filterRefsJob)) {
      return isCitationsLoading;
    }
  }

  const hasCitations = (getCitations(entry)?.length || 0) > 0;
  return hasCitations || isCitationsLoading;
};

// Returns true while the FILTER_RELEVANT_REFERENCES job exists and is non-terminal
// for entries that expect one. Drives isCitationsLoading so the spinner renders
// only during active filtering — not when the job hasn't arrived yet.
export const isFilterRelevantReferencesJobPending = (entry: ChatEntryDto | undefined): boolean => {
  if (!entry || entry.author === ChatEntryAuthorDto.USER) {
    return false;
  }
  if (FILTER_RELEVANT_REFERENCES_EXEMPT_PROMPT_TYPES.includes(entry.finalPromptType)) {
    return false;
  }
  const job = getPostProcessJob(entry, PostProcessJobTypeDto.FILTER_RELEVANT_REFERENCES);
  return !!job && !isPostProcessJobTerminal(job);
};

export const POST_PROCESS_POLL_INTERVAL_MS = 2000;
export const POST_PROCESS_POLL_MAX_FAILURES = 5;
export const POST_PROCESS_POLL_MAX_NOT_FOUND_ATTEMPTS = 30;

const POST_PROCESS_TERMINAL_STATUSES: PostProcessJobStatusDto[] = [
  PostProcessJobStatusDto.COMPLETED,
  PostProcessJobStatusDto.ERROR,
  PostProcessJobStatusDto.CANCELLED_TIMED_OUT,
  PostProcessJobStatusDto.CANCELLED_ADMIN_REQUEST,
];

export const isPostProcessJobTerminal = (job: PostProcessJobDto | undefined): boolean => {
  if (!job) {
    return false;
  }
  return POST_PROCESS_TERMINAL_STATUSES.includes(job.status);
};

// Prompt types that expect follow-up questions in study mode but not in tutor mode.
// Tutor mode drives its own Socratic questioning, so NACS explicitly skips the
// GENERATE_FOLLOW_UP_QUESTIONS dispatch for these — this set must mirror that
// suppression or the poller waits out its not-found cap on a job that never arrives.
// Primary tutor responses need no entry here: they omit follow-ups via their own
// PRIMARY_RESPONSE_TUTOR_MODE mapping. These three have no tutor-specific prompt type.
const TUTOR_MODE_FOLLOW_UP_SUPPRESSED_PROMPT_TYPES: FinalPromptType[] = [
  FinalPromptType.RESPOND_WITHOUT_CITATIONS,
  FinalPromptType.FLASHCARD_ACKNOWLEDGMENT,
  FinalPromptType.QUIZ_QUESTION_ACKNOWLEDGMENT,
];

// The post-process job types a bot entry is expected to spawn, looked up from
// JOBS_BY_PROMPT_TYPE. The polling coordinator waits on exactly this set; an
// unknown/absent prompt type yields no expected jobs.
export const getExpectedPostProcessJobTypes = (
  botEntry: ChatEntryDto,
  isTutorMode = false
): PostProcessJobTypeDto[] => {
  const jobTypes = JOBS_BY_PROMPT_TYPE[botEntry.finalPromptType] ?? [];
  // Tutor mode never generates follow-up questions, so don't poll for a job that
  // will never appear.
  if (isTutorMode && TUTOR_MODE_FOLLOW_UP_SUPPRESSED_PROMPT_TYPES.includes(botEntry.finalPromptType)) {
    return jobTypes.filter((type) => type !== PostProcessJobTypeDto.GENERATE_FOLLOW_UP_QUESTIONS);
  }
  return jobTypes;
};

export type PollPostProcessJobsDeps = {
  botEntry: ChatEntryDto;
  // Whether the entry was generated in tutor mode; feeds getExpectedPostProcessJobTypes
  // so RESPOND_WITHOUT_CITATIONS tutor answers don't poll for follow-up questions.
  isTutorMode?: boolean;
  // Defaults to getExpectedPostProcessJobTypes(botEntry, isTutorMode); accepted as an
  // override so tests can drive the coordinator with an explicit set.
  expectedJobTypes?: PostProcessJobTypeDto[];
  appStateRef: React.MutableRefObject<AppState>;
  setAppState: React.Dispatch<React.SetStateAction<AppState>>;
  // Unfiltered list of every post-process job for the entry.
  fetchJobsByEntry: (entryId: number) => Promise<PostProcessJobDto[]>;
  upsertJob: (job: PostProcessJobDto) => void;
  schedule?: (fn: () => void, delayMs: number) => void;
};

const addInProgressPostProcessJobId = (
  setAppState: React.Dispatch<React.SetStateAction<AppState>>,
  jobId: number
): void => {
  setAppState((prevState) => {
    const existing = prevState.inProgressPostProcessJobIds || [];
    if (existing.includes(jobId)) {
      return prevState;
    }
    return {
      ...prevState,
      inProgressPostProcessJobIds: [...existing, jobId],
    };
  });
};

const removeInProgressPostProcessJobId = (
  setAppState: React.Dispatch<React.SetStateAction<AppState>>,
  jobId: number
): void => {
  setAppState((prevState) => {
    const existing = prevState.inProgressPostProcessJobIds || [];
    if (!existing.includes(jobId)) {
      return prevState;
    }
    return {
      ...prevState,
      inProgressPostProcessJobIds: existing.filter((id) => id !== jobId),
    };
  });
};

// Polls all expected post-process jobs for a bot entry with a single unfiltered
// GET /nacs/post-process-jobs?entryId per tick. Upserts the most relevant returned
// job for each still-waiting expected type and stops once all expected types are
// terminal, or have been marked ERROR due to not-found/list-failure caps.
export const pollPostProcessJobs = (deps: PollPostProcessJobsDeps): void => {
  const {
    botEntry,
    expectedJobTypes = getExpectedPostProcessJobTypes(deps.botEntry, deps.isTutorMode),
    appStateRef,
    setAppState,
    fetchJobsByEntry,
    upsertJob,
    schedule = (fn, delayMs) => { setTimeout(fn, delayMs); },
  } = deps;

  if (expectedJobTypes.length === 0) {
    return;
  }

  // Types still being waited on; a type leaves the set once it is terminal or
  // dropped after the not-found cap.
  const waitSet = new Set<PostProcessJobTypeDto>(expectedJobTypes);
  const notFoundCounts = new Map<PostProcessJobTypeDto, number>();
  // Last job id observed per type. A job's id is stable for a given bot entry, so
  // the first row we see for a type fixes its id for the rest of the poll. Used to
  // attach the real jobId to bail-out telemetry and to unregister blocking ids on
  // give-up. Types that never produced a row (the genuine NOT_FOUND case) report no id.
  const lastSeenJobIdByType = new Map<PostProcessJobTypeDto, number>();
  // Job types whose id this coordinator has added to inProgressPostProcessJobIds
  // and is responsible for removing on terminal / give-up.
  const blockedTypes = new Set<PostProcessJobTypeDto>();

  const trackBailOut = (props: {
    jobType: PostProcessJobTypeDto;
    reason: PostProcessJobBailOutReason;
    attemptCount: number;
  }): void => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_POST_PROCESS_JOB_FAILED,
        props: {
          jobType: props.jobType,
          entryId: botEntry.id,
          chatId: botEntry.chatId,
          reason: props.reason,
          jobId: lastSeenJobIdByType.get(props.jobType),
          attemptCount: props.attemptCount,
        },
      },
    });
  };

  const tick = (failureCount: number): void => {
    fetchJobsByEntry(botEntry.id)
      .then((jobs) => {
        // NACS returns at most one job per type for an entry, so key by type
        // directly. (If multiple jobs per type ever become possible, this is where
        // a relevance/recency selection would go.)
        const jobByType = new Map<PostProcessJobTypeDto, PostProcessJobDto>();
        (jobs || []).forEach((job) => {
          jobByType.set(job.jobType, job);
        });

        Array.from(waitSet).forEach((jobType) => {
          const job = jobByType.get(jobType);

          if (!job) {
            const nextNotFoundCount = (notFoundCounts.get(jobType) || 0) + 1;
            notFoundCounts.set(jobType, nextNotFoundCount);
            if (nextNotFoundCount >= POST_PROCESS_POLL_MAX_NOT_FOUND_ATTEMPTS) {
              trackBailOut({
                jobType,
                reason: PostProcessJobBailOutReason.NOT_FOUND_CAP,
                attemptCount: nextNotFoundCount,
              });
              upsertJob({
                entryId: botEntry.id,
                chatId: botEntry.chatId,
                jobType,
                status: PostProcessJobStatusDto.ERROR,
              });
              waitSet.delete(jobType);
            }
            return;
          }

          upsertJob(job);

          if (job.id != null) {
            lastSeenJobIdByType.set(jobType, job.id);
          }

          // Block chat input while any post-process job is non-terminal; release it
          // once terminal. A job's id is stable per type for the entry, so a Set of
          // blocked types is enough to add/remove the id exactly once.
          if (job.id != null) {
            const isTerminal = POST_PROCESS_TERMINAL_STATUSES.includes(job.status);
            if (!isTerminal && !blockedTypes.has(jobType)) {
              blockedTypes.add(jobType);
              addInProgressPostProcessJobId(setAppState, job.id);
            } else if (isTerminal && blockedTypes.delete(jobType)) {
              removeInProgressPostProcessJobId(setAppState, job.id);
            }
          }

          if (POST_PROCESS_TERMINAL_STATUSES.includes(job.status)) {
            waitSet.delete(jobType);
          }
        });

        if (waitSet.size === 0) {
          return;
        }
        schedule(() => tick(0), POST_PROCESS_POLL_INTERVAL_MS);
      })
      .catch(() => {
        const nextFailureCount = failureCount + 1;
        if (nextFailureCount >= POST_PROCESS_POLL_MAX_FAILURES) {
          Array.from(waitSet).forEach((jobType) => {
            trackBailOut({
              jobType,
              reason: PostProcessJobBailOutReason.LOOKUP_HTTP_ERROR,
              attemptCount: nextFailureCount,
            });
            upsertJob({
              entryId: botEntry.id,
              chatId: botEntry.chatId,
              jobType,
              status: PostProcessJobStatusDto.ERROR,
            });
          });
          blockedTypes.forEach((jobType) => {
            const jobId = lastSeenJobIdByType.get(jobType);
            if (jobId != null) {
              removeInProgressPostProcessJobId(setAppState, jobId);
            }
          });
          return;
        }
        schedule(() => tick(nextFailureCount), POST_PROCESS_POLL_INTERVAL_MS);
      });
  };

  tick(0);
};

export const isAllowedMessageOrigin = (origin: string): boolean => {
  if (ELSCommonConfig.appProfile !== EnvProfile.prod) {
    return true;
  }

  return ALLOWED_MESSAGE_ORIGINS.some((allowedOrigin) => {
    if (allowedOrigin.startsWith('.')) {
      try {
        const { hostname } = new URL(origin);
        return hostname.endsWith(allowedOrigin) || hostname === allowedOrigin.slice(1);
      } catch {
        return false;
      }
    }

    return origin === allowedOrigin;
  });
};

export const getChatModeTypeLocalStorageKey = (appLinkData: AppLinkData, userId: string): string => {
  if (!appLinkData?.outPostBody?.context) {
    return null;
  }
  const { contextType, contextId } = getChatContextForNewChat({ appLinkData, userId } as AppState);

  if (!contextType || !contextId || !userId) {
    return null;
  }
  return `${CHAT_MODE_TYPE_STORAGE_KEY}-${contextType}-${contextId}-${userId}`;
};

export const getOrdinalSuffix = (n: number): string => {
    const j = n % 10;
    const k = n % 100;

    if (j === 1 && k !== 11) {
      return n + "st";
    }
    if (n === 2 && k !== 12) {
      return n + "nd";
    }
    if (j === 3 && k !== 13) {
      return n + "rd";
    }
    return n + "th";
};

export const getBookTitleWithEdition = (title: string, edition: number): string => {
  if (!title) {
    return null;
  }

  if (edition) {
    const ordinalEditionText = getOrdinalSuffix(edition);
    return `${title} — ${ordinalEditionText} Edition`;
  }

  return title;
};
